import PropTypes from 'prop-types';
import React from 'react';
import { Avatar } from '@DesignSystem/data-display';
import { Box } from '@material-ui/core';
import {
  getHistoryCardComment,
  getHistoryCardContentByType,
  getHistoryCardTitleByType
} from '../../utils/history';
import { MODEL_REGISTRY_HISTORY_TYPES } from '../../constants';
import classNames from './ModelRegistryHistory.module.scss';
import { DSCheckMarkIcon, DSCloseIcon } from '@ds-icons';
import { WebhookStatus } from '@model-registry/types';
import cx from 'classnames';
import { useGetWebhookResponseBodyByResponseId } from '@API/model-registry/useGetWebhookResponseBodyByResponseId';
import { useParams } from 'react-router';
import { useDispatch } from 'react-redux';
import alertsUtil from '@/util/alertsUtil';

const getMessage = webhook => {
  switch (webhook.status) {
    case WebhookStatus.SUCCESS:
      return `was successful with status code ${webhook.httpResponse.statusCode}. `;
    case WebhookStatus.HTTP_ERROR:
      return `failed with status code ${webhook.httpResponse.statusCode}. `;
    case WebhookStatus.INTERNAL_ERROR:
      return `failed with internal error ${webhook.internalError}. `;
    default:
      return '';
  }
};

const getIcon = status => {
  switch (status) {
    case WebhookStatus.SUCCESS:
      return <DSCheckMarkIcon width="12px" height="12px" />;
    default:
      return <DSCloseIcon width="12px" height="12px" />;
  }
};

const getStatusConfiguration = props => {
  const { previousValue, newValue } = props || {};
  const isChanged =
    previousValue?.statusConfiguration &&
    newValue?.statusConfiguration &&
    previousValue?.statusConfiguration !== newValue?.statusConfiguration;

  if (isChanged) {
    return (
      <>
        <b>Status configuration (old): </b> {previousValue?.statusConfiguration}
        <p>
          <b>Status configuration (new): </b> {newValue?.statusConfiguration}
        </p>
      </>
    );
  } else if (newValue?.statusConfiguration) {
    return (
      <>
        <b>Status configuration: </b> {newValue?.statusConfiguration}
      </>
    );
  }
  return null;
};

function ModelRegistryHistoryCard(props) {
  const { userName, userAvatarLink, date } = props;
  const { modelName, workspace: workspaceName } = useParams();

  const dispatch = useDispatch();
  const { mutate } = useGetWebhookResponseBodyByResponseId({
    onSuccess: data => {
      navigator.clipboard.writeText(JSON.stringify(data)).then(() => {
        dispatch(alertsUtil.openCopySuccessSnackbar());
      });
    }
  });

  const handleCopyResponse = responseId => {
    mutate({ modelName, workspaceName, responseId });
  };

  return (
    <div className={classNames.modelRegistryHistoryCardContainer}>
      <h6 className={classNames.modelRegistryHistoryCardTitle}>
        {getHistoryCardTitleByType(props)}
      </h6>
      <Box display="flex" alignItems="center">
        <div className="avatar-wrapper">
          <Avatar src={userAvatarLink} width={22} />
        </div>
        <p>
          <b data-mask-test="username">{userName} </b>
          {getHistoryCardContentByType(props)} on {date}
          <div>{getStatusConfiguration(props)}</div>
          <div>{getHistoryCardComment(props)}</div>
        </p>
      </Box>

      {props.webhooks?.map(webhook => {
        const message = getMessage(webhook.response);
        const icon = getIcon(webhook.response.status);
        return (
          <div
            key={webhook.id}
            className={classNames.modelRegistryHistoryWebhookContainer}
          >
            <div
              className={cx(
                classNames.modelRegistryHistoryWebhookStatusIcon,
                classNames[webhook.response.status]
              )}
            >
              {icon}
            </div>
            <p>
              <b>{webhook.webhookName} webhook</b> {message}
              {webhook.response.status !== WebhookStatus.INTERNAL_ERROR && (
                <u onClick={() => handleCopyResponse(webhook.responseId)}>
                  Copy full response
                </u>
              )}
            </p>
          </div>
        );
      })}
    </div>
  );
}

ModelRegistryHistoryCard.propTypes = {
  date: PropTypes.string.isRequired,
  userAvatarLink: PropTypes.string.isRequired,
  webhooks: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      status: PropTypes.oneOf(Object.values(WebhookStatus)).isRequired,
      httpResponse: PropTypes.shape({
        statusCode: PropTypes.number.isRequired
      }),
      internalError: PropTypes.string,
      webhookName: PropTypes.string.isRequired
    })
  ),
  newValue: PropTypes.shape({
    note: PropTypes.string
  }).isRequired,
  previousValue: PropTypes.shape({
    note: PropTypes.string
  }).isRequired,
  actionType: PropTypes.oneOf(Object.values(MODEL_REGISTRY_HISTORY_TYPES))
    .isRequired,
  userName: PropTypes.string.isRequired
};

export default ModelRegistryHistoryCard;
